import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Typography, alpha, useTheme,
} from '@mui/material';
import { ErrorOutline, Replay, TaskAlt } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import type { AdmissionApplication, AdmissionCycle } from '../../types/admissions';
import { admissionApplicationService } from '../../services/admissions/admissionApplicationService';
import { downloadXlsxBlob } from '../../utils/xlsx';
import { assessmentPlan, hasAnyScoring, pathwayFor } from '../../utils/admissionAssessment';
import { buildMarksTemplate, markableApplications, notYetMarkable, parseMarksSheet, type ParsedMarksRow } from '../../utils/bulkMarksXlsx';
import { ToneChip, type ChipTone } from '../common/StatusChip';
import StepDialog from '../common/StepDialog';
import ConfirmationDialog from '../common/ConfirmationDialog';
import BulkSheetPickStage from './BulkSheetPickStage';
import { TableCard, tableHeadCellSx, segmentedSx } from './tabLayout';

interface Props {
  open: boolean;
  cycle: AdmissionCycle;
  onClose: () => void;
  /** Marks were saved — refresh what's on screen. */
  onSaved: () => void;
}

type Stage = 'pick' | 'review' | 'result';
type Filter = 'all' | ParsedMarksRow['state'];

const STATE: Record<ParsedMarksRow['state'], { label: string; tone: ChipTone }> = {
  ready: { label: 'Ready', tone: 'success' },
  unchanged: { label: 'No change', tone: 'neutral' },
  error: { label: 'Needs fixing', tone: 'error' },
};

/**
 * Upload marks for many applicants from a spreadsheet: test papers,
 * interview rubric criteria (with recommendation, interviewer, comments)
 * and additional criteria. Rows are matched by application number; only changed marks are
 * sent; each applicant saves on its own through the same rules as entering
 * marks in the applicant drawer.
 */
const BulkMarksDialog: React.FC<Props> = ({ open, cycle, onClose, onSaved }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  // The sheet follows one pathway's papers and rounds; with several, pick one.
  const scoring = useMemo(() => (cycle.pathways || []).filter(p => hasAnyScoring(assessmentPlan(p))), [cycle.pathways]);
  const [pathwayKey, setPathwayKey] = useState(scoring[0]?.key);
  // No scoring pathway at all: the menu hides this dialog; the default keeps it renderable.
  const pathway = scoring.find(p => p.key === pathwayKey) ?? scoring[0] ?? pathwayFor(cycle, null);
  const plan = useMemo(() => assessmentPlan(pathway), [pathway]);

  const [stage, setStage] = useState<Stage>('pick');
  const [apps, setApps] = useState<AdmissionApplication[] | null>(null);
  const [building, setBuilding] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ParsedMarksRow[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [saving, setSaving] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [results, setResults] = useState<{ saved: number; failed: Array<{ name: string; message: string }> } | null>(null);

  // Every application in the cycle — rows are matched by GR, and a row for
  // someone who can't take marks yet gets a precise reason.
  useEffect(() => {
    if (!open) return;
    setStage('pick'); setRows([]); setResults(null); setFilter('all'); setApps(null);
    admissionApplicationService.listAllWithDetails({ cycleId: cycle._id })
      .then(r => setApps(r.items))
      .catch((err: any) => enqueueSnackbar(err?.message || 'Could not load the applicants', { variant: 'error' }));
  }, [open, cycle._id, enqueueSnackbar]);

  const pathwayApps = useMemo(
    () => (apps ? apps.filter(a => pathwayFor(cycle, a.classId).key === pathway.key) : null),
    [apps, cycle, pathway.key],
  );
  const markable = useMemo(() => (pathwayApps ? markableApplications(pathwayApps, plan) : []), [pathwayApps, plan]);
  const upcoming = useMemo(() => (pathwayApps ? notYetMarkable(pathwayApps, plan).length : 0), [pathwayApps, plan]);
  // Where applicants join the sheet: the assessment stage, or review when the
  // cycle only scores additional criteria.
  const joinsAt = plan.test || plan.interview ? 'once their assessment is scheduled' : 'once they are under review';
  const slug = [cycle.name, scoring.length > 1 ? pathway.name : ''].join(' ').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();

  const downloadTemplate = async () => {
    if (!apps) return;
    setBuilding(true);
    try {
      downloadXlsxBlob(`marks-${slug}.xlsx`, await buildMarksTemplate(pathway, pathwayApps ?? []));
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not build the marks sheet', { variant: 'error' });
    } finally {
      setBuilding(false);
    }
  };

  const readFile = async (file: File) => {
    if (!apps) return;
    setParsing(true);
    try {
      const parsed = await parseMarksSheet(file, cycle, pathway, apps);
      if (parsed.length === 0) throw new Error('No applicant rows found in the sheet.');
      setRows(parsed);
      setFileName(file.name);
      setFilter(parsed.some(r => r.state === 'error') ? 'error' : 'all');
      setStage('review');
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not read the file', { variant: 'error' });
    } finally {
      setParsing(false);
    }
  };

  // A reviewed upload is only thrown away on purpose.
  const handleClose = () => (stage === 'review' ? setDiscardOpen(true) : onClose());

  const ready = rows.filter(r => r.state === 'ready');
  const count = (s: ParsedMarksRow['state']) => rows.filter(r => r.state === s).length;
  const visible = filter === 'all' ? rows : rows.filter(r => r.state === filter);

  const save = async () => {
    setSaving(true);
    try {
      const out = await admissionApplicationService.bulkRecordMarks(cycle._id, ready.map(r => r.payload!));
      const nameOf = new Map(ready.map(r => [r.app!._id, r.name]));
      setResults({
        saved: out.filter(r => r.outcome === 'saved').length,
        failed: out.filter(r => r.outcome === 'failed').map(r => ({ name: nameOf.get(r.applicationId) ?? r.applicationId, message: r.message ?? 'Could not save.' })),
      });
      setStage('result');
      onSaved();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not save the marks', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const stages = (() => {
    const parts = [plan.test && 'test papers', plan.interview && 'interview rubrics', plan.custom && 'additional criteria'].filter(Boolean);
    return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}` : parts[0] ?? 'marks';
  })();

  const renderPick = () => (
    <BulkSheetPickStage
      templateText={apps == null
        ? 'Loading applicants…'
        : markable.length === 0
          ? <>No applicants are awaiting marks yet — they appear here {joinsAt}.{upcoming > 0 && <> <b>{upcoming}</b> applicant{upcoming === 1 ? ' is' : 's are'} still earlier in the process.</>}</>
          : <>One row for each of the <b>{markable.length}</b> applicant{markable.length === 1 ? '' : 's'} awaiting marks, with columns for {scoring.length > 1 ? <>{pathway.name}&apos;s</> : <>this cycle&apos;s</>} {stages}. Marks already entered are filled in.</>}
      templateDisabled={apps != null && markable.length === 0}
      templateButtonLabel="Download marks sheet (.xlsx)"
      buildingTemplate={building || apps == null}
      onDownloadTemplate={downloadTemplate}
      onFile={readFile}
      busy={parsing}
      busyText="Checking the marks…"
      capText=".xlsx · up to 500 applicants"
      tips={[
        'Rows are matched by application number — keep that column as it is.',
        'Leave a cell blank to keep the mark as it is. Write "Absent" for a test paper the applicant missed.',
        'An interview rubric needs every criterion filled; the round total is added up for you.',
        'Nothing is saved until you review the changes and confirm. Applicants move to Assessed once all their marks are in.',
      ]}
    />
  );

  const renderReview = () => (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
        <Box>
          <Typography variant="body2" fontWeight={600}>{fileName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {rows.length} row{rows.length === 1 ? '' : 's'} · <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>{count('ready')} to save</Box>
            {count('unchanged') > 0 && <> · {count('unchanged')} unchanged</>}
            {count('error') > 0 && <> · <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>{count('error')} need fixing</Box></>}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}>
            <ToggleButton value="all" sx={{ textTransform: 'none' }}>All {rows.length}</ToggleButton>
            <ToggleButton value="ready" sx={{ textTransform: 'none' }}>Ready {count('ready')}</ToggleButton>
            <ToggleButton value="error" sx={{ textTransform: 'none' }} disabled={count('error') === 0}>Needs fixing {count('error')}</ToggleButton>
          </ToggleButtonGroup>
          <Button size="small" startIcon={<Replay fontSize="small" />} onClick={() => setStage('pick')} sx={{ textTransform: 'none' }}>
            Upload another file
          </Button>
        </Stack>
      </Stack>
      {count('error') > 0 && (
        <Typography variant="body2" color="error.main">
          Rows that need fixing are left out. Correct them in the sheet and upload it again — rows already saved will show as unchanged.
        </Typography>
      )}
      <TableCard>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': tableHeadCellSx }}>
              <TableCell>Row</TableCell>
              <TableCell>Application no.</TableCell>
              <TableCell>Applicant</TableCell>
              <TableCell>What changes</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map(r => (
              <TableRow key={r.rowNumber}>
                <TableCell sx={{ color: 'text.secondary' }}>{r.rowNumber}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.applicationNumber || '—'}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{r.name || '—'}</TableCell>
                <TableCell>
                  {r.errors.length > 0
                    ? r.errors.map(e => <Typography key={e} variant="body2" color="error.main">{e}</Typography>)
                    : r.changes.length > 0
                      ? <Typography variant="body2">{r.changes.join(' · ')}</Typography>
                      : <Typography variant="body2" color="text.secondary">Same as already recorded</Typography>}
                </TableCell>
                <TableCell><ToneChip label={STATE[r.state].label} tone={STATE[r.state].tone} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableCard>
    </Stack>
  );

  const renderResult = () => {
    const allGood = !results?.failed.length;
    return (
      <Box sx={{ textAlign: 'center', py: { xs: 2, sm: 4 }, maxWidth: 480, mx: 'auto' }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: '50%', mx: 'auto', mb: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(allGood ? theme.palette.success.main : theme.palette.warning.main, 0.12),
          color: allGood ? 'success.main' : 'warning.main',
        }}>
          {allGood ? <TaskAlt sx={{ fontSize: 34 }} /> : <ErrorOutline sx={{ fontSize: 34 }} />}
        </Box>
        <Typography variant="h6" fontWeight={700}>
          Marks saved for {results?.saved} applicant{results?.saved === 1 ? '' : 's'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The merit list has been updated. Applicants with every mark in are now Assessed.
        </Typography>
        {!allGood && (
          <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, textAlign: 'left', bgcolor: alpha(theme.palette.warning.main, 0.08) }}>
            <Typography variant="body2" fontWeight={600}>{results!.failed.length} not saved</Typography>
            {results!.failed.map(f => (
              <Typography key={f.name} variant="body2" color="text.secondary">{f.name} — {f.message}</Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const footer = (
    <>
      <Button onClick={handleClose} disabled={saving} sx={{ textTransform: 'none', color: 'text.secondary' }}>
        {stage === 'result' ? 'Close' : 'Cancel'}
      </Button>
      <Box sx={{ flex: 1 }} />
      {stage === 'review' && (
        <Button
          variant="contained"
          disableElevation
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={save}
          disabled={saving || ready.length === 0}
          sx={{ textTransform: 'none' }}
        >
          {saving ? 'Saving marks…' : `Save marks for ${ready.length} applicant${ready.length === 1 ? '' : 's'}`}
        </Button>
      )}
      {stage === 'result' && (
        <Button variant="contained" disableElevation onClick={onClose} sx={{ textTransform: 'none' }}>Done</Button>
      )}
    </>
  );

  return (
    <>
      <StepDialog
        open={open}
        onClose={handleClose}
        closeDisabled={saving}
        title="Upload marks"
        subtitle={cycle.name}
        steps={['Prepare', 'Review', 'Done']}
        activeStep={stage === 'pick' ? 0 : stage === 'review' ? 1 : 3}
        maxWidth={stage === 'review' ? 'lg' : 'md'}
        footer={footer}
      >
        {stage === 'pick' && scoring.length > 1 && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2.5 }}>
            <Typography variant="body2" color="text.secondary">Marks for</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={pathway.key}
              onChange={(_, v) => v && setPathwayKey(v)}
              sx={segmentedSx}
            >
              {scoring.map(p => <ToggleButton key={p.key} value={p.key}>{p.name}</ToggleButton>)}
            </ToggleButtonGroup>
          </Stack>
        )}
        {stage === 'pick' && renderPick()}
        {stage === 'review' && renderReview()}
        {stage === 'result' && renderResult()}
      </StepDialog>
      <ConfirmationDialog
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        title="Discard this upload?"
        message="No marks have been saved yet. The uploaded file is not kept anywhere."
        confirmText="Discard"
        cancelText="Keep reviewing"
        confirmColor="error"
      />
    </>
  );
};

export default BulkMarksDialog;
